function normalizeText(value) {
  return String(value ?? "").trim();
}

function normalizeMoneyLike(value) {
  return normalizeText(value).replace(/,/g, ".");
}

function normalizeDebtKey(debt) {
  return [
    normalizeText(debt?.creditor).toLowerCase(),
    normalizeMoneyLike(debt?.amount),
    normalizeMoneyLike(debt?.installment ?? debt?.installment_amount),
    normalizeText(debt?.dueDate ?? debt?.due_date),
  ].join("|");
}

function findCallDebtIndicesFromList(debts, callDebts) {
  const buckets = new Map();

  debts.forEach((debt, index) => {
    const key = normalizeDebtKey(debt);
    const next = buckets.get(key) || [];
    next.push(index);
    buckets.set(key, next);
  });

  const matched = [];
  for (const debt of Array.isArray(callDebts) ? callDebts : []) {
    const key = normalizeDebtKey(debt);
    const bucket = buckets.get(key);
    if (!bucket?.length) continue;
    matched.push(bucket.shift());
  }

  return matched.sort((a, b) => a - b);
}

export function getDebtsArray(source) {
  if (Array.isArray(source?.debts) && source.debts.length) {
    return source.debts;
  }
  return source?.debt ? [source.debt] : [];
}

export function getCallDebtEntries(source, debtsInput = null) {
  const debts = Array.isArray(debtsInput) ? debtsInput : getDebtsArray(source);
  if (!debts.length) return [];

  const hasExplicitForCall = debts.some((debt) => typeof debt?.forCall === "boolean");
  if (hasExplicitForCall) {
    return debts.reduce((acc, debt, index) => {
      if (debt?.forCall) acc.push({ debt, index });
      return acc;
    }, []);
  }

  const matchedIndices = findCallDebtIndicesFromList(debts, source?.callDebts);
  if (matchedIndices.length) {
    return matchedIndices.map((index) => ({ debt: debts[index], index }));
  }

  const idxRaw = Number(source?.primaryDebtIndex);
  if (Number.isFinite(idxRaw)) {
    const index = Math.max(0, Math.min(idxRaw, debts.length - 1));
    return [{ debt: debts[index], index }];
  }

  return [{ debt: debts[0], index: 0 }];
}

export function getCallDebts(source, debtsInput = null) {
  return getCallDebtEntries(source, debtsInput).map((entry) => entry.debt);
}

export function pickPrimaryCallDebt(source, debtsInput = null, fallbackDebt = null) {
  const debts = Array.isArray(debtsInput) ? debtsInput : getDebtsArray(source);
  const [entry] = getCallDebtEntries(source, debts);
  if (entry?.debt) return entry.debt;
  if (source?.debt && typeof source.debt === "object") return source.debt;
  if (debts[0] && typeof debts[0] === "object") return debts[0];
  if (fallbackDebt && typeof fallbackDebt === "object") return fallbackDebt;
  return null;
}

export function getPaidDebtsArr(source) {
  if (Array.isArray(source?.paidDebts)) return source.paidDebts;
  if (Array.isArray(source?.paid_debts)) return source.paid_debts;
  return null;
}

function readPaidFlag(value) {
  return !!(value?.paid ?? value?.isPaid ?? value?.is_paid);
}

export function isDebtPaid(source, debt, index) {
  if (debt && typeof debt === "object" && "paid" in debt) return !!debt.paid;

  const paidArr = getPaidDebtsArr(source);
  if (paidArr?.[index]) return readPaidFlag(paidArr[index]);

  if (paidArr?.length && debt) {
    const creditor = normalizeText(debt?.creditor);
    const amount = normalizeMoneyLike(debt?.amount);
    const hit = paidArr.find((item) => {
      const itemCreditor = normalizeText(item?.creditor);
      const itemAmount = normalizeMoneyLike(item?.amount);
      return itemCreditor === creditor && itemAmount === amount;
    });
    if (hit) return readPaidFlag(hit);
  }

  return false;
}
